import React from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { ItemCard } from 'tradera/components/item-card/item-card';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import { useAppDispatch } from 'tradera/state/hooks';
import { Carousel } from 'tradera/components/carousel/carousel';
import uiActions from 'tradera/state/ui/actions';
import * as itemActions from 'tradera/state/items/actions';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { useInView } from 'react-intersection-observer';
import { useGetRecentlyViewed } from './use-get-recently-viewed';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';

export const RecentlyViewed = () => {
    const { t } = useTranslator();
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    const items = useGetRecentlyViewed(loadingTriggerInView);

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    const openQuickView = (item: PossibleItemTypes) => {
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: 'Start Page Recently Viewed'
            })
        );
    };

    const toggleWishList = (itemId: number) => {
        dispatch(itemActions.wishlist(itemId));
    };

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {items?.length > 0 ? (
                <>
                    <StartPageSectionTitle>
                        {t('recentlyViewed_header')}
                    </StartPageSectionTitle>
                    <Carousel
                        id="recently-viewed-carousel"
                        numberItemsToShow={numberCarouselCards}
                        numberItemsToScroll={numberCarouselCards}
                        className="mx-md-3 mx-lg-2 mx-xl-0"
                        padX>
                        <LeftButton size="md" alignVerticallyToItemCards />
                        <CarouselContent>
                            {items.map((item) => (
                                <ItemCard
                                    // This is to make the parent container set grid gaps instead
                                    // of individual cards keeping paddings and margins set of their own.
                                    // Makes displaying cards in different situations more predictable.
                                    className="p-0 m-0"
                                    item={item}
                                    key={item.itemId}
                                    onAddToWishlist={toggleWishList}
                                    onRemoveFromWishlist={toggleWishList}
                                    onQuickView={openQuickView}
                                    showFallback={!item.itemUrl}
                                    gaCategory="Startpage recently viewed"
                                    showBidStatus
                                    showTitleOnOneLine
                                    showShippingOrOnDisplay={false}
                                />
                            ))}
                        </CarouselContent>
                        <RightButton size="md" alignVerticallyToItemCards />
                    </Carousel>
                </>
            ) : null}
        </StartPageSection>
    );
};
